import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import * as bookingApi from '@/services/bookingApi';
import type { Customer, CustomerNote, CustomerActivity } from '@/types';
import { ArrowLeft, Star, Ban, CheckCircle, Clock, XCircle, FileText, Trash2 } from 'lucide-react';

const CustomerDetail: React.FC = () => {
  const { id: customerId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [customer, setCustomer] = useState<Customer | null>(null);
  const [notes, setNotes] = useState<CustomerNote[]>([]);
  const [activities, setActivities] = useState<CustomerActivity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Ban modal state
  const [showBanModal, setShowBanModal] = useState(false);
  const [banReason, setBanReason] = useState('');
  const [isBanning, setIsBanning] = useState(false);

  // Note form state
  const [newNote, setNewNote] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [deletingNoteId, setDeletingNoteId] = useState<string | null>(null);

  useEffect(() => {
    if (!customerId) return;
    fetchCustomerData();
  }, [customerId]);

  const fetchCustomerData = async () => {
    if (!customerId) return;
    setIsLoading(true);
    setError(null);

    try {
      const [customerData, notesData, activitiesData] = await Promise.all([
        bookingApi.getCustomer(customerId),
        bookingApi.getCustomerNotes(customerId),
        bookingApi.getCustomerActivities(customerId),
      ]);

      setCustomer(customerData);
      setNotes(notesData);
      setActivities(activitiesData);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load customer details');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBanCustomer = async () => {
    if (!banReason.trim() || !customerId) return;

    setIsBanning(true);
    try {
      const updatedCustomer = await bookingApi.banCustomer(customerId, banReason);
      setCustomer(updatedCustomer);
      setShowBanModal(false);
      setBanReason('');
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to ban customer');
    } finally {
      setIsBanning(false);
    }
  };

  const handleUnbanCustomer = async () => {
    if (!customerId) return;
    try {
      const updatedCustomer = await bookingApi.unbanCustomer(customerId);
      setCustomer(updatedCustomer);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to unban customer');
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim() || !customerId) return;

    setIsAddingNote(true);
    try {
      const addedNote = await bookingApi.addCustomerNote(customerId, newNote, isPrivate);
      setNotes([addedNote, ...notes]);
      setNewNote('');
      setIsPrivate(false);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to add note');
    } finally {
      setIsAddingNote(false);
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    if (!confirm('Are you sure you want to delete this note?') || !customerId) return;

    setDeletingNoteId(noteId);
    try {
      await bookingApi.deleteCustomerNote(customerId, noteId);
      setNotes(notes.filter((n) => n.id !== noteId));
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to delete note');
    } finally {
      setDeletingNoteId(null);
    }
  };

  const renderSatisfactionStars = (score?: number) => {
    if (score === undefined || score === null) {
      return <span className="text-gray-400 text-sm">No rating</span>;
    }

    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-4 h-4 ${star <= score ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
          />
        ))}
        <span className="ml-1 text-sm text-gray-600">{score}/5</span>
      </div>
    );
  };

  const getActivityIcon = (type: CustomerActivity['type']) => {
    switch (type) {
      case 'booking_made':
        return <FileText className="w-5 h-5 text-blue-500" />;
      case 'no_show':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'late_cancellation':
        return <Clock className="w-5 h-5 text-orange-500" />;
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      default:
        return <FileText className="w-5 h-5 text-gray-500" />;
    }
  };

  const getActivityLabel = (type: CustomerActivity['type']) => {
    switch (type) {
      case 'booking_made':
        return 'Booking Made';
      case 'no_show':
        return 'No Show';
      case 'late_cancellation':
        return 'Late Cancellation';
      case 'completed':
        return 'Appointment Completed';
      default:
        return type;
    }
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (error || !customer) {
    return (
      <div className="p-6">
        <button
          onClick={() => navigate('/dashboard/customers')}
          className="flex items-center gap-2 text-indigo-600 hover:text-indigo-800 mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Customers
        </button>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error || 'Customer not found'}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Back Button */}
      <button
        onClick={() => navigate('/dashboard/customers')}
        className="flex items-center gap-2 text-indigo-600 hover:text-indigo-800 mb-6"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Customers
      </button>

      {/* Customer Header */}
      <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{customer.name}</h1>
            <div className="mt-2 space-y-1">
              <p className="text-gray-600">
                <span className="font-medium">Email:</span> {customer.email}
              </p>
              {customer.phone && (
                <p className="text-gray-600">
                  <span className="font-medium">Phone:</span> {customer.phone}
                </p>
              )}
              <div className="mt-2">
                <span className="font-medium text-gray-600">Satisfaction:</span>
                <div className="mt-1">{renderSatisfactionStars(customer.satisfaction_score)}</div>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Ban Status Badge */}
            {customer.banned ? (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
                <Ban className="w-4 h-4 mr-1" />
                Banned
              </span>
            ) : (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                <CheckCircle className="w-4 h-4 mr-1" />
                Active
              </span>
            )}

            {/* Ban/Unban Button */}
            {customer.banned ? (
              <button
                onClick={handleUnbanCustomer}
                className="px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700"
              >
                Unban Customer
              </button>
            ) : (
              <button
                onClick={() => setShowBanModal(true)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700"
              >
                Ban Customer
              </button>
            )}
          </div>
        </div>

        {/* Ban Info */}
        {customer.banned && customer.banned_reason && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">
              <span className="font-medium">Ban Reason:</span> {customer.banned_reason}
            </p>
            {customer.banned_at && (
              <p className="text-sm text-red-600 mt-1">
                Banned on: {new Date(customer.banned_at).toLocaleDateString()}
              </p>
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Notes Section */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Notes</h2>

          {/* Add Note Form */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <textarea
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder="Add a note about this customer..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
            />
            <div className="flex justify-between items-center mt-3">
              <label className="flex items-center gap-2 text-sm text-gray-600">
                <input
                  type="checkbox"
                  checked={isPrivate}
                  onChange={(e) => setIsPrivate(e.target.checked)}
                  className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                Private note
              </label>
              <button
                onClick={handleAddNote}
                disabled={!newNote.trim() || isAddingNote}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isAddingNote ? 'Adding...' : 'Add Note'}
              </button>
            </div>
          </div>

          {/* Notes List */}
          <div className="space-y-4">
            {notes.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No notes yet</p>
            ) : (
              notes.map((note) => (
                <div key={note.id} className="border-b border-gray-200 pb-4 last:border-b-0 last:pb-0">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-gray-900">{note.provider_name}</span>
                        {note.is_private && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                            Private
                          </span>
                        )}
                      </div>
                      <p className="text-gray-700 whitespace-pre-wrap">{note.note}</p>
                      <p className="text-xs text-gray-500 mt-1">{formatDateTime(note.created_at)}</p>
                    </div>
                    <button
                      onClick={() => handleDeleteNote(note.id)}
                      disabled={deletingNoteId === note.id}
                      className="ml-3 p-1 text-gray-400 hover:text-red-600 disabled:opacity-50"
                      title="Delete note"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Activity Timeline */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Activity Timeline</h2>

          <div className="space-y-4">
            {activities.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No activities yet</p>
            ) : (
              <div className="relative">
                {/* Timeline line */}
                <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-gray-200" />

                {activities.map((activity) => (
                  <div key={activity.id} className="relative flex gap-4 pb-6 last:pb-0">
                    {/* Icon */}
                    <div className="relative z-10 w-10 h-10 rounded-full bg-white flex items-center justify-center border-2 border-gray-200">
                      {getActivityIcon(activity.type)}
                    </div>

                    {/* Content */}
                    <div className="flex-1 pt-1">
                      <p className="font-medium text-gray-900">{getActivityLabel(activity.type)}</p>
                      <p className="text-sm text-gray-500">{formatDateTime(activity.created_at)}</p>
                      {activity.metadata && Object.keys(activity.metadata).length > 0 && (
                        <div className="mt-1 text-xs text-gray-400">
                          {activity.metadata.booking_id && <span>Booking ID: {activity.metadata.booking_id}</span>}
                          {activity.metadata.service_name && <span> • {activity.metadata.service_name}</span>}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Ban Confirmation Modal */}
      {showBanModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6 border-b">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-gray-900">Ban Customer</h2>
                <button
                  onClick={() => {
                    setShowBanModal(false);
                    setBanReason('');
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="p-6">
              <p className="text-gray-600 mb-4">
                Are you sure you want to ban <span className="font-medium">{customer.name}</span>? This will prevent them from making new bookings.
              </p>

              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason for ban <span className="text-red-500">*</span>
              </label>
              <textarea
                value={banReason}
                onChange={(e) => setBanReason(e.target.value)}
                placeholder="Enter the reason for banning this customer..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                autoFocus
              />
            </div>

            <div className="p-6 border-t bg-gray-50 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowBanModal(false);
                  setBanReason('');
                }}
                className="px-4 py-2 text-gray-700 font-medium hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleBanCustomer}
                disabled={!banReason.trim() || isBanning}
                className="px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isBanning ? 'Banning...' : 'Ban Customer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerDetail;